import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../components/AuthContext';
import { FaDownload, FaEye, FaCalendarAlt, FaUser, FaRupeeSign, FaReceipt, FaMoneyCheckAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ProformaInvoiceReports = () => {
  const { user } = useContext(AuthContext);
  const companyId = user?.user?.company?._id || user?.company?._id || user?.user?.company || user?.company;
  const [proformaInvoices, setProformaInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [salesPersonFilter, setSalesPersonFilter] = useState('');
  const [salesPersons, setSalesPersons] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAdvance, setTotalAdvance] = useState(0);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchProformaInvoices();
    fetchSalesPersons();
  }, []);

  const fetchSalesPersons = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (companyId) params.append('companyId', companyId);
      
      const response = await axios.get(`${API_BASE}/reports/proforma-invoices/employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesPersons(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching sales persons:', error);
    }
  };

  const fetchProformaInvoices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      if (companyId) params.append('companyId', companyId);

      const response = await axios.get(`${API_BASE}/reports/proforma-invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProformaInvoices(response.data.proformaInvoices || []);
      setTotalAmount(response.data.totalAmount || 0);
      setTotalAdvance(response.data.totalAdvance || 0);
    } catch (error) {
      console.error('Error fetching proforma invoices:', error);
      toast.error('Failed to fetch proforma invoice reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchProformaInvoices();
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);

      const response = await axios.get(`${API_BASE}/reports/proforma-invoices/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proforma-invoices-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Proforma Invoice Reports</h2>
        <p className="text-gray-600">View and analyze proforma invoices issued by date and sales person with advance details</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sales Person</label>
            <select
              value={salesPersonFilter}
              onChange={(e) => setSalesPersonFilter(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">All Sales Persons</option>
              {salesPersons.map(person => (
                <option key={person._id} value={person._id}>
                  {person.fullName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={handleFilter}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FaEye className="mr-2" />
              Filter
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <FaDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaReceipt className="text-blue-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-800">{proformaInvoices.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaRupeeSign className="text-green-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Invoice Amount</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaMoneyCheckAlt className="text-orange-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Advance</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalAdvance)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaUser className="text-purple-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Average per Invoice</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(proformaInvoices.length > 0 ? totalAmount / proformaInvoices.length : 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Proforma Invoices</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading proforma invoices...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PI Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales Person
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PI Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Advance Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {proformaInvoices.length > 0 ? (
                  proformaInvoices.map((invoice, index) => (
                    <tr key={invoice._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(invoice.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {invoice.invoiceNumber || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.createdBy?.fullName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {/* Customer name will need to be derived from quotation */}
                        N/A
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">
                        {formatCurrency(invoice.advanceAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          invoice.status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : invoice.status === 'partial'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {invoice.status || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No proforma invoices found for the selected criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProformaInvoiceReports;
